import {
  OptimizerConfig,
  ShippedHazardousGoods,
  TrafficOptions,
  TruckOptions,
  TruckType,
  TunnelCategory,
} from '@nx-smartmonkey/shared/domain';
import { Plan } from '../../domain/plan/Plan';
import { PlanDTO } from '../../domain/plan/PlanDTO';
import { PlanRepositoryDTO } from '../../domain/plan/PlanRepositoryDTO';
import { ProjectCustomFields } from '../../domain/project/ProjectCustomFields';
import { DriverMapper } from '../driver/DriverMapper';
import { GeoFenceMapper } from '../geoFence/GeoFenceMapper';
import { StopMapper } from '../stop/StopMapper';

export class PlanMapper {
  static toDomain(props: PlanDTO, projectCustomFields: ProjectCustomFields): Plan {
    const {
      id,
      organization_id,
      project_id,
      status,
      execution_date,
      label,
      optimizer_config,
      completed_stops,
      canceled_stops,
      pending_stops,
      total_stops,
      total_drivers,
      created_by,
      created_at,
      updated_at,
      deleted_at,
      deleted,
      stops,
      drivers,
      geo_fences,
      traffic_options,
    } = props;

    const optimizerConfigDomain = optimizer_config ? OptimizerConfig.create(optimizer_config) : undefined;

    let trafficOptions: TrafficOptions | undefined;

    if (traffic_options) {
      const { truck, ...other } = traffic_options ?? {};
      const truckOptions = truck
        ? TruckOptions.create({
            shipped_hazardous_goods: truck?.shipped_hazardous_goods as ShippedHazardousGoods[],
            gross_weight: truck?.gross_weight,
            height: truck?.height,
            width: truck?.width,
            length: truck?.length,
            tunnel_category: truck?.tunnel_category as TunnelCategory,
            axle_count: truck?.axle_count,
            type: truck?.type as TruckType,
            trailer_count: truck?.trailer_count,
          })
        : undefined;
      trafficOptions = TrafficOptions.create({
        ...other,
        truck: truckOptions,
      });
    }

    return Plan.create(
      {
        organization_id,
        project_id,
        status,
        execution_date,
        label,
        optimizer_config: optimizerConfigDomain,
        traffic_options: trafficOptions,
        drivers: drivers.map((stop) => DriverMapper.toDomain(stop, projectCustomFields)),
        stops: stops.map((stop) => StopMapper.toDomain(stop, projectCustomFields)),
        geo_fences: geo_fences.map((geo) => GeoFenceMapper.toDomain(geo)),
        completed_stops,
        canceled_stops,
        pending_stops,
        total_stops,
        total_drivers,
        created_by,
        created_at,
        updated_at,
        deleted_at,
        deleted,
      },
      id
    );
  }

  static toRepository(plan: Plan): PlanRepositoryDTO {
    return {
      label: plan.label,
      status: plan.status,
      execution_date: plan.execution_date,
      optimizer_config: plan.optimizer_config?.getValue(),
      ...(plan.traffic_options
        ? {
            traffic_options: {
              multiplier: plan.traffic_options.multiplier,
              departure_at: plan.traffic_options.departure_at,
              mode: plan.traffic_options.mode,
              truck: plan.traffic_options.truck ? plan.traffic_options.truck.getValue() : undefined,
            },
          }
        : {}),
      geo_fences: plan.geo_fences.map((geo) => geo.id),
      drivers: plan.drivers.map((driver) => DriverMapper.toRepository(driver)),
    };
  }

  static toDTO(plan: Plan): PlanDTO {
    return {
      id: plan.id,
      organization_id: plan.organization_id,
      project_id: plan.project_id,
      status: plan.status,
      execution_date: plan.execution_date,
      label: plan.label,
      optimizer_config: plan.optimizer_config
        ? {
            max_wait_time: plan.optimizer_config.max_wait_time,
            first_solution_strategy: plan.optimizer_config.first_solution_strategy,
            local_search_strategy: plan.optimizer_config.local_search_strategy,
            time_limit_seconds: plan.optimizer_config.time_limit_seconds,
            lns_time_limit: plan.optimizer_config.lns_time_limit,
            balance_services: plan.optimizer_config.balance_services,
            service_duration: plan.optimizer_config.service_duration,
            operation_country: plan.optimizer_config.operation_country,
            distance_span_coefficient: plan.optimizer_config.distance_span_coefficient,
            time_span_coefficient: plan.optimizer_config.time_span_coefficient,
            use_distance: plan.optimizer_config.use_distance,
            distance_balance: plan.optimizer_config.distance_balance,
            drop_penalty: plan.optimizer_config.drop_penalty,
            isolation_factor: plan.optimizer_config.isolation_factor,
          }
        : undefined,
      traffic_options: plan.traffic_options
        ? {
            multiplier: plan.traffic_options.multiplier,
            departure_at: plan.traffic_options.departure_at,
            mode: plan.traffic_options.mode ?? `car`,
            truck: plan.traffic_options.truck,
          }
        : undefined,
      completed_stops: plan.completed_stops,
      incomplete_stops: plan.incomplete_stops,
      canceled_stops: plan.canceled_stops,
      pending_stops: plan.pending_stops,
      total_stops: plan.total_stops,
      total_drivers: plan.total_drivers,
      created_at: plan.created_at,
      created_by: plan.created_by,
      updated_at: plan.updated_at,
      deleted_at: plan.deleted_at,
      deleted: plan.deleted,
      stops: plan.stops.map(StopMapper.toDTO),
      drivers: plan.drivers.map(DriverMapper.toDTO),
      geo_fences: plan.geo_fences.map(GeoFenceMapper.toDTO),
    };
  }
}
